import { v4 as uuidv4 } from 'uuid';
import { indexedDBHelper } from '../utils/indexedDBHelper';

export interface VideoMetadata {
  id: string;
  name: string;
  size: number;
  type: string;
  uploadedAt: Date;
  uploadedBy: string;
  url: string;
  thumbnailUrl?: string;
}

class AzureBlobService {
  private accountName: string;
  private containerName: string;
  private blobUrlCache: Map<string, string> = new Map();

  constructor() {
    this.accountName = process.env.REACT_APP_AZURE_STORAGE_ACCOUNT_NAME!;
    this.containerName = process.env.REACT_APP_AZURE_STORAGE_CONTAINER_NAME!;

    if (!this.accountName || !this.containerName) {
      throw new Error('Azure Storage configuration is missing. Please check your environment variables.');
    }
  }

  // Upload file using backend API
  private async uploadToAzure(file: File, fileName: string, onProgress?: (progress: number) => void): Promise<string> {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      
      // Check if backend is available
      try {
        const healthCheck = await fetch(`${backendUrl}/health`);
        if (!healthCheck.ok) {
          throw new Error('Backend not available');
        }
        const healthData = await healthCheck.json();
        console.log('🔗 Backend health:', healthData);
        
        // Check if backend is in demo mode
        if (healthData.azure === 'demo-mode' || healthData.status === 'demo') {
          console.warn('⚠️ Backend is in demo mode, using local storage instead');
          return this.uploadDemo(file, fileName, onProgress);
        }
      } catch (healthError) {
        console.warn('⚠️ Backend API not available, using demo mode');
        return this.uploadDemo(file, fileName, onProgress);
      }

      console.log('🔄 Uploading to Azure via real backend API...');

      // Use FormData for proper multipart upload
      const formData = new FormData();
      formData.append('video', file);
      formData.append('userEmail', 'user@example.com'); // You can get this from auth context

      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        // Track upload progress
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable && onProgress) {
            const progress = Math.round((event.loaded / event.total) * 100);
            onProgress(progress);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status === 200) {
            try {
              const response = JSON.parse(xhr.responseText);
              console.log('✅ Real Azure upload successful:', response.url);
              console.log('📊 Upload details:', response);
              resolve(response.url);
            } catch (parseError) {
              reject(new Error('Invalid response from backend'));
            }
          } else {
            console.error('❌ Backend upload failed:', xhr.status, xhr.responseText);
            reject(new Error(`Backend upload failed: ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          console.error('❌ Network error during upload');
          reject(new Error('Network error during upload'));
        });

        // Send FormData (multipart) - backend now supports this
        xhr.open('POST', `${backendUrl}/api/upload/video`);
        xhr.send(formData);
      });

    } catch (error) {
      console.error('Error in backend upload:', error);
      // Fallback to demo mode
      return this.uploadDemo(file, fileName, onProgress);
    }
  }

  // Demo upload for when backend is not available - stores file in IndexedDB
  private async uploadDemo(file: File, fileName: string, onProgress?: (progress: number) => void): Promise<string> {
    console.log('🎭 Using demo mode - storing video file in IndexedDB for playback');
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress(progress);
        }
      };
      
      reader.onload = async () => {
        try {
          const arrayBuffer = reader.result as ArrayBuffer;
          const fileKey = `video_file_${fileName}`;
          
          // Check file size
          const fileSizeMB = file.size / 1024 / 1024;
          console.log(`📦 Video size: ${fileSizeMB.toFixed(2)}MB, File type: ${file.type}`);
          
          // IndexedDB can handle much larger files (typically 50% of available disk space)
          if (fileSizeMB > 500) {
            console.warn('⚠️ File too large (>500MB). Consider using real Azure storage.');
            reject(new Error('File too large for demo mode. Please use a smaller file (<500MB) or configure Azure storage.'));
            return;
          }
          
          // Store the binary data with metadata
          const videoData = {
            data: arrayBuffer,
            mimeType: file.type || 'video/mp4',
            fileName: file.name
          };
          
          // Store in IndexedDB
          await indexedDBHelper.saveVideo(fileKey, videoData);
          console.log('✅ Demo upload completed - file stored in IndexedDB and playable');
          
          // Return the key as the "URL"
          resolve(fileKey);
        } catch (error) {
          console.error('Error storing video file:', error);
          reject(new Error('Failed to store video. Please try again or use a smaller file.'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      
      // Read file as ArrayBuffer for efficient storage
      reader.readAsArrayBuffer(file);
    });
  }

  async uploadVideo(
    file: File,
    userEmail: string,
    onProgress?: (progress: number) => void
  ): Promise<VideoMetadata> {
    try {
      const fileId = uuidv4();
      const fileName = `${fileId}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      
      console.log('Starting upload to Azure Blob Storage...', {
        fileName,
        fileSize: file.size,
        fileType: file.type,
        account: this.accountName,
        container: this.containerName
      });
      
      // Upload to Azure Blob Storage
      const blobUrl = await this.uploadToAzure(file, fileName, onProgress);
      
      console.log('Upload completed successfully:', blobUrl);

      // Create video metadata
      const videoMetadata: VideoMetadata = {
        id: fileId,
        name: file.name,
        size: file.size,
        type: file.type,
        uploadedAt: new Date(),
        uploadedBy: userEmail,
        url: blobUrl,
      };

      // Save metadata to IndexedDB
      await this.saveVideoMetadata(videoMetadata);

      return videoMetadata;
    } catch (error) {
      console.error('Error uploading video to Azure:', error);
      throw new Error(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private async saveVideoMetadata(metadata: VideoMetadata): Promise<void> {
    try {
      const existingVideos = await this.getVideosAsync();
      existingVideos.push(metadata);
      await indexedDBHelper.saveMetadata('allVideos', existingVideos);
      console.log(`✅ Saved metadata. Total videos: ${existingVideos.length}`);
    } catch (error) {
      console.error('Error saving video metadata:', error);
      throw error;
    }
  }

  async getVideosAsync(): Promise<VideoMetadata[]> {
    try {
      // Try IndexedDB first
      const stored = await indexedDBHelper.getMetadata('allVideos');
      if (stored && Array.isArray(stored)) {
        return stored.map((video: any) => ({
          ...video,
          uploadedAt: new Date(video.uploadedAt)
        }));
      }
      
      // Fallback to localStorage for migration
      const localStored = localStorage.getItem('videoMetadata');
      if (localStored) {
        const videos = JSON.parse(localStored);
        // Migrate to IndexedDB
        await indexedDBHelper.saveMetadata('allVideos', videos);
        console.log('📦 Migrated video metadata from localStorage to IndexedDB');
        return videos.map((video: any) => ({
          ...video,
          uploadedAt: new Date(video.uploadedAt)
        }));
      }
      
      return [];
    } catch (error) {
      console.error('Error retrieving videos:', error);
      return [];
    }
  }

  getVideos(): VideoMetadata[] {
    // Synchronous wrapper for backward compatibility
    // Note: This will return empty array initially and populate on next tick
    let videos: VideoMetadata[] = [];
    this.getVideosAsync().then(v => videos = v);
    
    // Try localStorage for immediate sync access (legacy)
    try {
      const stored = localStorage.getItem('videoMetadata');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.map((video: any) => ({
          ...video,
          uploadedAt: new Date(video.uploadedAt)
        }));
      }
    } catch (error) {
      console.error('Error retrieving videos from localStorage:', error);
    }
    
    return videos;
  }

  async getVideoUrl(videoKey: string): Promise<string> {
    // If it's a local storage key, get from IndexedDB and create Blob URL
    if (videoKey.startsWith('video_file_')) {
      // Check cache first for instant loading
      if (this.blobUrlCache.has(videoKey)) {
        console.log('✅ Using cached Blob URL (instant load)');
        return this.blobUrlCache.get(videoKey)!;
      }
      
      try {
        console.log('📦 Loading video from IndexedDB...');
        const data = await indexedDBHelper.getVideo(videoKey);
        if (!data) {
          throw new Error('Video data not found in storage');
        }
        
        // Handle both old format (string) and new format (object with metadata)
        let arrayBuffer: ArrayBuffer;
        let mimeType: string = 'video/mp4';
        
        if (typeof data === 'string') {
          // Old format - try to use as-is (though this may not work)
          console.warn('⚠️ Old video format detected, attempting to convert');
          return data;
        } else if (data.data instanceof ArrayBuffer) {
          // New format with metadata
          arrayBuffer = data.data;
          mimeType = data.mimeType || 'video/mp4';
        } else {
          throw new Error('Invalid video data format');
        }
        
        console.log(`🎬 Creating Blob URL for video with MIME type: ${mimeType}`);
        
        // Create Blob from ArrayBuffer
        const blob = new Blob([arrayBuffer], { type: mimeType });
        
        // Create and return Blob URL
        const blobUrl = URL.createObjectURL(blob);
        
        // Cache the blob URL for instant subsequent loads
        this.blobUrlCache.set(videoKey, blobUrl);
        console.log('✅ Blob URL created and cached successfully');
        
        return blobUrl;
      } catch (error) {
        console.error('Error creating Blob URL:', error);
        throw new Error(`Failed to load video: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
    return videoKey;
  }

  async deleteVideo(videoId: string): Promise<void> {
    try {
      // Delete from Azure Blob Storage
      const videos = this.getVideos();
      const video = videos.find(v => v.id === videoId);
      
      if (video) {
        // If it's a local file, delete from IndexedDB
        if (video.url.startsWith('video_file_')) {
          await indexedDBHelper.deleteVideo(video.url);
        } else {
          // Delete from Azure using REST API
          try {
            const url = new URL(video.url);
            const blobName = url.pathname.substring(url.pathname.lastIndexOf('/') + 1);
            const deleteUrl = `https://${this.accountName}.blob.core.windows.net/${this.containerName}/${blobName}`;
            
            const response = await fetch(deleteUrl, {
              method: 'DELETE',
              headers: {
                'x-ms-version': '2022-11-02',
              },
            });
            
            if (!response.ok && response.status !== 404) {
              console.warn('Failed to delete from Azure:', response.status);
            }
          } catch (azureError) {
            console.warn('Could not delete from Azure (file may not exist):', azureError);
          }
        }
      }

      // Remove from metadata storage
      const updatedVideos = videos.filter(v => v.id !== videoId);
      await indexedDBHelper.saveMetadata('allVideos', updatedVideos);
      console.log(`✅ Deleted video. Remaining videos: ${updatedVideos.length}`);
    } catch (error) {
      console.error('Error deleting video:', error);
      throw new Error('Failed to delete video');
    }
  }

  generateSasUrl(videoId: string): string {
    try {
      const videos = this.getVideos();
      const video = videos.find(v => v.id === videoId);
      
      if (!video) {
        throw new Error('Video not found');
      }

      // Return the direct blob URL (since container is public for reading)
      return video.url;
    } catch (error) {
      console.error('Error generating SAS URL:', error);
      throw new Error('Failed to generate video URL');
    }
  }
}

export const azureBlobService = new AzureBlobService();